import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { FaChevronDown, FaComments } from 'react-icons/fa';

import { RootState } from '../../store';
import { ICollabSyncUi, IOrderRevisionLogEntry } from '../../store/order/orderStore';
import {
    fetchOrderActivityLog,
    setHighlightOrderLine,
} from '../../store/order/actions';
import {
    hasOtherUserActivity,
    isEntryFromOtherUser,
} from '../../utils/orderActivityUtils';
import {
    isActivityFabVisible,
} from '../../utils/orderActivityFabStorage';
import Order from '../../models/Order';
import {
    isCollabActive,
    isOnDashboardPath,
    isOrderEditableLocally,
    resolveCurrentEditableOrderContext,
} from '../../utils/orderRouteUtils';
import OrderActivityPanel from './OrderActivityPanel';

interface StateProps {
    entries: IOrderRevisionLogEntry[];
    isLoading: boolean;
    order: Order | undefined;
    dashboardOrder: Order | undefined;
    orderNotFound: boolean;
    orderRemotelyUnavailableReason: 'closed' | 'deleted' | 'unavailable' | null;
    userEmail: string | null;
    collabActiveNonce: number;
    activityFabVisibleNonce: number;
    collabSyncUi: ICollabSyncUi;
}

interface DispatchProps {
    fetchOrderActivityLog: (orderId: number, options?: { incremental?: boolean }) => Promise<void>;
    setHighlightOrderLine: (lineId: number | null) => void;
}

type Props = StateProps & DispatchProps & RouteComponentProps;

interface ComponentState {
    expanded: boolean;
    unreadCount: number;
}

class OrderActivityMessenger extends React.Component<Props, ComponentState> {
    lastSeenEntryId: number | null = null;
    messengerRef = React.createRef<HTMLDivElement>();

    state: ComponentState = {
        expanded: false,
        unreadCount: 0,
    };

    componentDidMount() {
        this.bootstrap();
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentDidUpdate(prevProps: Props) {
        const prevOrderId = this.getOrderId(prevProps);
        const orderId = this.getOrderId();

        if (orderId !== prevOrderId || prevProps.location.pathname !== this.props.location.pathname) {
            this.bootstrap();
            return;
        }

        const orderBecameEditable =
            isOrderEditableLocally(this.props.order || this.props.dashboardOrder) &&
            !isOrderEditableLocally(prevProps.order || prevProps.dashboardOrder) &&
            !!orderId;

        if (orderBecameEditable) {
            this.bootstrap();
            return;
        }

        if (prevProps.collabActiveNonce !== this.props.collabActiveNonce) {
            this.bootstrap();
            return;
        }

        if (prevProps.activityFabVisibleNonce !== this.props.activityFabVisibleNonce) {
            const orderId = this.getOrderId();
            const manuallyVisible = !!(orderId && isActivityFabVisible(this.props.userEmail, orderId));
            this.bootstrap(manuallyVisible);
            return;
        }

        const wasVisible = this.shouldShow(prevProps);
        const isVisible = this.shouldShow();

        if (wasVisible && !isVisible && this.state.expanded) {
            this.setState({ expanded: false });
        }

        if (!this.state.expanded && this.props.entries !== prevProps.entries) {
            this.updateUnreadCount();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    getOrderContext = (props: Props = this.props) =>
        resolveCurrentEditableOrderContext(
            {
                order: props.order,
                dashboardOrder: props.dashboardOrder,
            },
            props.location.pathname
        );

    getOrderId = (props: Props = this.props) => {
        const context = this.getOrderContext(props);
        return context ? context.orderId : null;
    };

    shouldShow = (props: Props = this.props) => {
        if (props.orderNotFound || props.orderRemotelyUnavailableReason) {
            return false;
        }

        const context = this.getOrderContext(props);
        if (!context) {
            return false;
        }

        const { orderId } = context;
        const login = props.userEmail;

        if (isActivityFabVisible(login, orderId)) {
            return true;
        }

        if (!isCollabActive(login, orderId)) {
            return false;
        }

        const otherActivity = hasOtherUserActivity(props.entries, login);
        const syncingFromRemote = props.collabSyncUi && props.collabSyncUi.status === 'syncing';
        return otherActivity || syncingFromRemote || this.state.unreadCount > 0;
    };

    handleClickOutside = (event: MouseEvent) => {
        if (!this.state.expanded || !this.messengerRef.current) {
            return;
        }

        if (!this.messengerRef.current.contains(event.target as Node)) {
            this.setState({ expanded: false });
        }
    };

    bootstrap = (openPanel = false) => {
        this.lastSeenEntryId = null;
        this.setState({ unreadCount: 0, expanded: openPanel });

        const context = this.getOrderContext();
        if (!context) {
            return;
        }

        const { orderId } = context;
        const manuallyVisible = isActivityFabVisible(this.props.userEmail, orderId);

        if (!manuallyVisible && !isCollabActive(this.props.userEmail, orderId)) {
            return;
        }

        this.refresh(false);
    };

    refresh = (incremental: boolean) => {
        const orderId = this.getOrderId();
        if (!orderId) {
            return;
        }

        this.props.fetchOrderActivityLog(orderId, { incremental });
    };

    updateUnreadCount = () => {
        const { entries } = this.props;
        if (!entries.length) {
            return;
        }

        const latestId = entries[0].id;
        if (this.lastSeenEntryId === null) {
            this.lastSeenEntryId = latestId;
            return;
        }

        const newEntries = entries.filter(
            (entry) =>
                entry.id > this.lastSeenEntryId! &&
                isEntryFromOtherUser(entry, this.props.userEmail)
        );
        if (newEntries.length) {
            this.setState((prev) => ({ unreadCount: prev.unreadCount + newEntries.length }));
            this.lastSeenEntryId = latestId;
        }
    };

    markSeen = () => {
        const { entries } = this.props;
        if (entries.length) {
            this.lastSeenEntryId = entries[0].id;
        }
        this.setState({ unreadCount: 0 });
    };

    toggleExpanded = () => {
        this.setState((prev) => {
            const expanded = !prev.expanded;
            if (expanded) {
                this.markSeen();
            }
            return { expanded };
        });
    };

    handleEntryClick = (lineId: number | null) => {
        if (!lineId) {
            return;
        }

        const orderId = this.getOrderId();
        const onDashboard = isOnDashboardPath(this.props.location.pathname);

        if (!onDashboard && orderId) {
            this.props.history.push(`/dashboard/${orderId}`);
        }

        window.setTimeout(() => {
            this.props.setHighlightOrderLine(lineId);
            const element = document.getElementById(`order-line-${lineId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            window.setTimeout(() => this.props.setHighlightOrderLine(null), 2500);
        }, onDashboard ? 150 : 600);
    };

    render() {
        if (!this.shouldShow()) {
            return null;
        }

        const { entries, isLoading } = this.props;
        const { expanded, unreadCount } = this.state;

        return (
            <div
                className={`order-activity-messenger${expanded ? ' order-activity-messenger--open' : ''}`}
                ref={this.messengerRef}
            >
                {expanded && (
                    <div className="order-activity-messenger__panel">
                        <div className="order-activity-messenger__panel-header">
                            <div>
                                <h4 className="order-activity-messenger__title">Order activity</h4>
                                <p className="order-activity-messenger__subtitle">Who changed what on this order</p>
                            </div>
                            <button
                                type="button"
                                className="order-activity-messenger__collapse"
                                onClick={this.toggleExpanded}
                                aria-label="Collapse activity"
                            >
                                <FaChevronDown aria-hidden />
                            </button>
                        </div>
                        <OrderActivityPanel
                            entries={entries}
                            isLoading={isLoading}
                            maxEntries={40}
                            onLineClick={this.handleEntryClick}
                            className="order-activity-panel--embedded"
                            showHeader={false}
                            showProductThumbnails={true}
                            showActorCustomerName={false}
                            compactLineLabels={true}
                        />
                    </div>
                )}
                <button
                    type="button"
                    className="order-activity-messenger__fab"
                    onClick={this.toggleExpanded}
                    aria-expanded={expanded}
                    title="Order activity"
                >
                    <FaComments aria-hidden />
                    <span className="order-activity-messenger__fab-label">Activity</span>
                    {!expanded && unreadCount > 0 && (
                        <span className="order-activity-messenger__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    )}
                </button>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    const orderState = state.order.order;

    return {
        entries: orderState.orderActivity.entries,
        isLoading: orderState.orderActivity.isLoading,
        order: orderState.order,
        dashboardOrder: orderState.dashboardOrder,
        orderNotFound: orderState.orderNotFound,
        orderRemotelyUnavailableReason: orderState.orderRemotelyUnavailableReason,
        userEmail: state.user.user.user && state.user.user.user.email
            ? state.user.user.user.email
            : null,
        collabActiveNonce: orderState.collabActiveNonce,
        activityFabVisibleNonce: orderState.activityFabVisibleNonce,
        collabSyncUi: orderState.collabSyncUi,
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>): DispatchProps => ({
    fetchOrderActivityLog: async (orderId, options) => {
        await dispatch(fetchOrderActivityLog(orderId, options));
    },
    setHighlightOrderLine: (lineId) => {
        dispatch(setHighlightOrderLine(lineId));
    },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrderActivityMessenger));
